import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { billingApi } from "@/api";
import { StripeCardForm } from "@/components/billing/stripe-card-form";
import { ConfirmModal } from "@/components/ui/confirm-modal";
import { Radius, Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { notify } from "@/lib/dialog";
import { formatAmount } from "@/lib/format-currency";
import {
  isTrialing,
  subscriptionBillingLine,
} from "@/lib/subscription-status";
import { useAuthStore } from "@/state/auth-store";
import type { SubscriptionSummary } from "@/types";
import i18n from "@/i18n";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

function formatDate(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(i18n.resolvedLanguage, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

const brandLabel = (brand: string) =>
  brand
    ? brand.charAt(0).toUpperCase() + brand.slice(1)
    : i18n.t("billing.subscription.card");

export default function SubscriptionScreen() {
  useTranslation();
  const router = useRouter();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);
  const refreshUser = useAuthStore((s) => s.refreshUser);

  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const reloadSummary = useCallback(async () => {
    try {
      setSummary(await billingApi.subscription());
    } catch {
      // Keep the last-known summary on a transient refresh failure.
    }
  }, []);

  const onCardSaved = useCallback(async () => {
    setShowCardForm(false);
    notify(
      i18n.t("billing.subscription.updatedTitle"),
      i18n.t("billing.subscription.updatedBody"),
    );
    await reloadSummary();
  }, [reloadSummary]);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const s = await billingApi.subscription();
        if (active) setSummary(s);
      } catch {
        if (active) {
          notify(
            i18n.t("billing.subscription.loadError"),
            i18n.t("billing.common.tryAgainLater"),
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const close = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace("/profile" as never);
  }, [router]);

  const confirmCancel = useCallback(async () => {
    setBusy(true);
    try {
      setSummary(await billingApi.cancel());
      await refreshUser();
      setConfirmCancelOpen(false);
    } catch {
      notify(
        i18n.t("billing.subscription.cancelError"),
        i18n.t("billing.common.tryAgainLater"),
      );
    } finally {
      setBusy(false);
    }
  }, [refreshUser]);

  const onResume = useCallback(async () => {
    setBusy(true);
    try {
      setSummary(await billingApi.resume());
      await refreshUser();
    } catch {
      notify(
        i18n.t("billing.subscription.resumeError"),
        i18n.t("billing.common.tryAgainLater"),
      );
    } finally {
      setBusy(false);
    }
  }, [refreshUser]);

  const intervalWord = i18n.t(
    summary?.interval === "year"
      ? "billing.subscription.perYear"
      : "billing.subscription.perMonth",
  );
  const endDate = formatDate(summary?.currentPeriodEnd);
  const cancelling = summary?.cancelAtPeriodEnd === true;
  // A trial is a real subscription in `trialing` status, so without this it
  // renders identically to a paying one — "your next billing date is …" over
  // a price the person has not paid and may never pay.
  const trialing = isTrialing(summary?.status);
  const billingLine = subscriptionBillingLine({
    status: summary?.status,
    cancelAtPeriodEnd: summary?.cancelAtPeriodEnd,
  });
  const priceText = `${formatAmount(summary?.amount, summary?.currency)} ${intervalWord}`;

  /**
   * A store subscription — App Store or Play, via RevenueCat.
   *
   * Until this existed the summary came back `active: false` for these
   * accounts and the screen rendered "You're on the Free plan" with an
   * Upgrade button, telling a paying subscriber they had not paid.
   */
  const storeManaged = summary?.provider === "store";
  // Named from the platform in hand. We do not store which store sold the
  // subscription, and this is the store the person can actually open from
  // this device — on the rare cross-platform case the copy below still says
  // "the app store you subscribed through" is where it is managed.
  const androidStore = Platform.OS === "android";
  const storeTitle = i18n.t(
    androidStore
      ? "billing.subscription.storeTitleAndroid"
      : "billing.subscription.storeTitle",
  );
  const storeManageLabel = i18n.t(
    androidStore
      ? "billing.subscription.storeManageAndroid"
      : "billing.subscription.storeManage",
  );
  const openStoreSubscriptions = useCallback(async () => {
    const url = androidStore
      ? "https://play.google.com/store/account/subscriptions"
      : "https://apps.apple.com/account/subscriptions";
    try {
      await Linking.openURL(url);
    } catch {
      notify(
        i18n.t("billing.subscription.storeOpenError"),
        i18n.t("billing.common.tryAgainLater"),
      );
    }
  }, [androidStore]);

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style="auto" />

      <View style={styles.topBar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={i18n.t("billing.common.goBackA11y")}
          onPress={close}
          hitSlop={10}
          style={({ pressed }) => [
            styles.iconButton,
            pressed && styles.pressed,
          ]}
        >
          <Ionicons name="chevron-back" size={23} color={c.tint} />
        </Pressable>
        <Text style={styles.topBarTitle}>
          {i18n.t("billing.subscription.title")}
        </Text>
        <View style={styles.headerSpacer} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={c.tint} />
        </View>
      ) : !summary?.active ? (
        <View style={styles.center}>
          <Text style={styles.emptyTitle}>
            {i18n.t("billing.subscription.freeTitle")}
          </Text>
          <Text style={styles.emptyBody}>
            {i18n.t("billing.subscription.freeBody")}
          </Text>
          <Pressable
            accessibilityRole="button"
            onPress={() => router.replace("/pricing" as never)}
            style={({ pressed }) => [
              styles.primaryBtn,
              pressed && styles.pressed,
            ]}
          >
            <Text style={styles.primaryBtnText}>
              {i18n.t("billing.subscription.seePlans")}
            </Text>
          </Pressable>
        </View>
      ) : storeManaged ? (
        /**
         * A store subscription. The card states where it lives, when access
         * runs to, and sends them to the store to change anything.
         *
         * It shows NO amount and NO cancel button: the price is not something
         * the store tells us, and the cancel switch belongs to Apple or
         * Google — a button here would be one the app cannot honour.
         *
         * The date IS shown, from the store's own expiry. Where somebody paid
         * should not change what they are told about their own subscription.
         * The wording is deliberately "access runs to" rather than "renews on"
         * or "ends on": auto-renew is a store-side setting we are not told
         * about, so either of those would be a claim we cannot stand behind.
         */
        <ScrollView contentContainerStyle={styles.content}>
          <Text style={styles.sectionLabel}>
            {i18n.t("billing.subscription.current")}
          </Text>
          <View style={styles.card}>
            <View style={styles.planRow}>
              <View style={styles.planIcon}>
                <Ionicons name="sparkles" size={18} color={c.onTint} />
              </View>
              <View style={styles.flex}>
                <Text style={styles.planTitle}>Opncue Plus</Text>
                <Text style={styles.planPrice}>{storeTitle}</Text>
              </View>
            </View>

            <View style={styles.divider} />

            {endDate ? (
              <Text style={styles.billingLine}>
                {i18n.t("billing.subscription.storeAccessUntil", {
                  date: endDate,
                })}
              </Text>
            ) : null}

            <Text style={styles.billingLine}>
              {i18n.t("billing.subscription.storeBody")}
            </Text>

            <Pressable
              accessibilityRole="button"
              onPress={openStoreSubscriptions}
              style={({ pressed }) => [
                styles.primaryBtn,
                pressed && styles.pressed,
              ]}
            >
              <Text style={styles.primaryBtnText}>{storeManageLabel}</Text>
            </Pressable>
          </View>
        </ScrollView>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {/* Current subscription */}
          <Text style={styles.sectionLabel}>
            {i18n.t("billing.subscription.current")}
          </Text>
          <View style={styles.card}>
            <View style={styles.planRow}>
              <View style={styles.planIcon}>
                <Ionicons name="sparkles" size={18} color={c.onTint} />
              </View>
              <View style={styles.flex}>
                <View style={styles.planTitleRow}>
                  <Text style={styles.planTitle}>Opncue Plus</Text>
                  {/* Named at the top, not only in the small print: the whole
                      point is that someone should not have to read a date to
                      work out they have not paid yet. */}
                  {trialing ? (
                    <View style={styles.trialBadge}>
                      <Text style={styles.trialBadgeText}>
                        {i18n.t("billing.subscription.trialBadge")}
                      </Text>
                    </View>
                  ) : null}
                </View>
                <Text style={styles.planPrice}>{priceText}</Text>
              </View>
            </View>

            <View style={styles.divider} />

            <Text style={styles.billingLine}>
              {billingLine === "trial_converts"
                ? i18n.t("billing.subscription.trialConverts", {
                    date: endDate,
                    amount: formatAmount(summary.amount, summary.currency),
                    interval: intervalWord,
                  })
                : billingLine === "trial_cancelled"
                  ? i18n.t("billing.subscription.trialCancelled", {
                      date: endDate,
                    })
                  : billingLine === "ends_on"
                    ? i18n.t("billing.subscription.endsOn", { date: endDate })
                    : i18n.t("billing.subscription.nextBilling", {
                        date: endDate,
                      })}
            </Text>

            {summary.paymentMethod ? (
              <Text style={styles.billingSubtle}>
                {brandLabel(summary.paymentMethod.brand)} ••••{" "}
                {summary.paymentMethod.last4}
              </Text>
            ) : null}

            {cancelling ? (
              <Pressable
                accessibilityRole="button"
                disabled={busy}
                onPress={onResume}
                style={({ pressed }) => [
                  styles.primaryBtn,
                  busy && styles.disabled,
                  pressed && !busy && styles.pressed,
                ]}
              >
                {busy ? (
                  <ActivityIndicator size="small" color={c.onTint} />
                ) : (
                  <Text style={styles.primaryBtnText}>
                    {i18n.t("billing.subscription.resume")}
                  </Text>
                )}
              </Pressable>
            ) : (
              <Pressable
                accessibilityRole="button"
                onPress={() => setConfirmCancelOpen(true)}
                style={({ pressed }) => [
                  styles.dangerBtn,
                  pressed && styles.pressed,
                ]}
              >
                <Text style={styles.dangerBtnText}>
                  {i18n.t("billing.subscription.cancel")}
                </Text>
              </Pressable>
            )}
          </View>

          {/* Payment method */}
          <Text style={styles.sectionLabel}>
            {i18n.t("billing.subscription.paymentMethod")}
          </Text>
          <View style={styles.card}>
            {summary.paymentMethod ? (
              <View style={styles.pmRow}>
                <Ionicons name="card-outline" size={22} color={c.text} />
                <View style={styles.flex}>
                  <Text style={styles.pmTitle}>
                    {brandLabel(summary.paymentMethod.brand)} ••••{" "}
                    {summary.paymentMethod.last4}
                  </Text>
                  <Text style={styles.pmSubtle}>
                    {i18n.t("billing.subscription.expires", {
                      month: String(summary.paymentMethod.expMonth).padStart(
                        2,
                        "0",
                      ),
                      year: summary.paymentMethod.expYear,
                    })}
                  </Text>
                </View>
              </View>
            ) : (
              <Text style={styles.pmSubtle}>
                {i18n.t("billing.subscription.noCard")}
              </Text>
            )}

            {showCardForm ? (
              <StripeCardForm
                onDone={onCardSaved}
                onCancel={() => setShowCardForm(false)}
              />
            ) : Platform.OS === "web" ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => setShowCardForm(true)}
                style={({ pressed }) => [
                  styles.outlineBtn,
                  pressed && styles.pressed,
                ]}
              >
                <Text style={styles.outlineBtnText}>
                  {summary.paymentMethod
                    ? i18n.t("billing.subscription.updatePayment")
                    : i18n.t("billing.subscription.addPayment")}
                </Text>
              </Pressable>
            ) : (
              <Text style={styles.pmSubtle}>
                {i18n.t("billing.common.manageOnWeb")}
              </Text>
            )}
          </View>
        </ScrollView>
      )}

      <ConfirmModal
        visible={confirmCancelOpen}
        icon="sad-outline"
        destructive
        // Cancelling a trial is a different act from cancelling a paid plan:
        // one is "don't charge me at all", the other is "stop charging me
        // after what I've already paid for". The paid copy promises a billing
        // period they never bought.
        title={i18n.t(
          trialing
            ? "billing.subscription.cancelTrialTitle"
            : "billing.subscription.cancelTitle",
        )}
        message={i18n.t(
          trialing
            ? "billing.subscription.cancelTrialBody"
            : "billing.subscription.cancelBody",
        )}
        confirmLabel={i18n.t(
          trialing
            ? "billing.subscription.cancelTrialConfirm"
            : "billing.subscription.cancel",
        )}
        cancelLabel={i18n.t("billing.subscription.keepPlus")}
        // Keep Plus is the safe action: solid green. Cancel is de-emphasised —
        // no fill, gray text, gray outline.
        cancelVariant="filled"
        cancelColor={c.success}
        confirmVariant="ghost"
        confirmColor={c.textSecondary}
        busy={busy}
        onConfirm={confirmCancel}
        onCancel={() => {
          if (!busy) setConfirmCancelOpen(false);
        }}
      />
    </SafeAreaView>
  );
}

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    safe: { flex: 1, backgroundColor: c.background },
    flex: { flex: 1 },
    topBar: {
      minHeight: 56,
      paddingHorizontal: Spacing.md,
      flexDirection: "row",
      alignItems: "center",
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: "center",
      justifyContent: "center",
    },
    topBarTitle: {
      flex: 1,
      color: c.text,
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      fontWeight: "700",
      textAlign: "center",
    },
    headerSpacer: { width: 40 },
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: Spacing.xl,
      gap: Spacing.md,
    },
    emptyTitle: {
      color: c.text,
      fontFamily: FONT_FAMILY,
      fontSize: 20,
      fontWeight: "800",
    },
    emptyBody: {
      color: c.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 22,
      textAlign: "center",
      maxWidth: 340,
    },
    content: {
      width: "100%",
      maxWidth: 640,
      alignSelf: "center",
      paddingHorizontal: Spacing.xl,
      paddingTop: Spacing.xl,
      paddingBottom: Spacing.xxxl,
      gap: Spacing.sm,
    },
    sectionLabel: {
      color: c.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "800",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginTop: Spacing.lg,
      marginBottom: Spacing.xs,
    },
    card: {
      padding: Spacing.lg,
      borderRadius: Radius.md,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surface,
      gap: Spacing.md,
    },
    planRow: { flexDirection: "row", alignItems: "center", gap: Spacing.md },
    planIcon: {
      width: 38,
      height: 38,
      borderRadius: 19,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: c.tint,
    },
    planTitleRow: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
    },
    planTitle: {
      color: c.text,
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      fontWeight: "800",
    },
    trialBadge: {
      backgroundColor: c.backgroundSelected,
      borderRadius: 999,
      paddingHorizontal: 8,
      paddingVertical: 2,
    },
    trialBadgeText: {
      color: c.tint,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      fontWeight: "700",
    },
    planPrice: {
      color: c.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "600",
      marginTop: 2,
    },
    divider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: c.border,
    },
    billingLine: {
      color: c.text,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 22,
    },
    billingSubtle: {
      color: c.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      marginTop: -Spacing.xs,
    },
    pmRow: { flexDirection: "row", alignItems: "center", gap: Spacing.md },
    pmTitle: {
      color: c.text,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "700",
    },
    pmSubtle: {
      color: c.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      marginTop: 2,
    },
    primaryBtn: {
      minHeight: 46,
      marginTop: Spacing.xs,
      paddingHorizontal: Spacing.xl,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.sm,
      backgroundColor: c.tint,
    },
    primaryBtnText: {
      color: c.onTint,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "800",
    },
    dangerBtn: {
      minHeight: 46,
      marginTop: Spacing.xs,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.sm,
      borderWidth: 1,
      borderColor: c.danger,
    },
    dangerBtnText: {
      color: c.danger,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "800",
    },
    outlineBtn: {
      minHeight: 44,
      marginTop: Spacing.xs,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.sm,
      borderWidth: 1,
      borderColor: c.border,
    },
    outlineBtnText: {
      color: c.text,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "700",
    },
    disabled: { opacity: 0.6 },
    pressed: { opacity: 0.72 },
  });
}
